import { Check, MapPin } from '@phosphor-icons/react';
import type { Location } from '@shh/shared';
import { Button, Icon, TextField } from '@shh/ui';
import { useEffect, useState } from 'react';
import { searchPlaces } from '../lib/geo';
import { settingsText } from '../i18n/settings';

/** City search for weather and daylight – only the town, never an address. */
export function PlaceField({
  value,
  onChange,
}: {
  value?: Location;
  onChange: (l?: Location) => void;
}) {
  const [q, setQ] = useState(value?.name ?? '');
  const [hits, setHits] = useState<(Location & { area?: string })[]>([]);
  useEffect(() => {
    if (value && q === value.name) return;
    const t = window.setTimeout(
      () =>
        void searchPlaces(q)
          .then(setHits)
          .catch(() => setHits([])),
      300,
    );
    return () => window.clearTimeout(t);
  }, [q, value]);
  return (
    <div className="flex flex-col gap-gap-sm">
      <TextField
        label={settingsText.place}
        description={settingsText.placeHint}
        value={q}
        onChange={(v) => {
          setQ(v);
          onChange(undefined);
        }}
      />
      {!value && hits.length > 0 && (
        <ul className="flex flex-col gap-gap-xs" aria-label={settingsText.placeSuggestions}>
          {hits.map((h) => (
            <li key={`${h.latitude},${h.longitude}`}>
              <Button
                variant="secondary"
                icon={MapPin}
                fullWidth
                className="justify-start"
                onPress={() => {
                  onChange({
                    name: h.name,
                    latitude: h.latitude,
                    longitude: h.longitude,
                    ...(h.region ? { region: h.region } : {}),
                  });
                  setQ(h.name);
                  setHits([]);
                }}
              >
                {h.name} <span className="font-normal text-text-secondary">· {h.area}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {value && (
        <p className="flex items-center gap-gap-xs text-sm font-semibold text-feedback-success">
          <Icon icon={Check} size="sm" weight="bold" /> {value.name}
        </p>
      )}
    </div>
  );
}
